import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import Content from '~/containers/Content';
import PageContainer from '~/containers/PageContainer';

import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterLoginSection from '~/components/FooterLoginSection';
import FooterReturnNotice from '~/components/FooterReturnNotice';
import GoTopButton from '~/components/GoTopButton';
import LeaveOrCancelGroupModal from '~/components/LeaveOrCancelGroupModal';
import LocationBar from '~/components/LocationBar';
import PreOrderTimeBar from '~/components/PreOrderTimeBar';

import {
  ActivityCarousel,
  FixedHeaderSection,
  GroupingBar,
  GroupingNotice,
  OrderHeaderNotice,
  PreorderTimeBar,
  SearchInput,
  StoreFilterModal,
  TagFilterModal,
} from './components';

import DisplayMode from './DisplayMode';
import SearchMode from './SearchMode';

import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

import { useNavigate } from 'react-router-dom';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import authAtom from '~/recoil/atom/auth';
import filterMealSearchFormAtom from '~/recoil/atom/filterMealSearchForm';
import groupHostInfoAtom from '~/recoil/atom/groupHostInfo';
import modalControlAtom from '~/recoil/atom/modalControl';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import platformAtom from '~/recoil/atom/platform';
import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import isHostSelector from '~/recoil/selectors/isHost';

import useCartId from '~/hooks/useCartId';
import useSearchItem from '~/hooks/useSearchItem';
import useSearchStore from '~/hooks/useSearchStore';
import useStoreList from '~/hooks/useStoreList';
import usePinnedItems from '~/pages/Home/hooks/usePinnedItems';
import usePopularItems from './hooks/usePopularItems';

import * as fp from 'lodash/fp';

import parseISO from 'date-fns/parseISO';

import getDateInventory from '~/utils/getDateInventory';
import isCanPreOrderTime from '~/utils/isCanPreOrderTime';
import isInOperatingTime from '~/utils/isInOperatingTime';
import isNotInHoliday from '~/utils/isNotInHoliday';
import transformDateRangeToString from '~/utils/transformDateRangeToString';

import { CartType } from '~/types/cart';
import useOrders from '../OrderList/hooks/useOrders';

export default function Home() {
  const setModalControl = useSetRecoilState(modalControlAtom);
  const auth = useRecoilValue(authAtom);
  const isHost = useRecoilValue(isHostSelector);
  const platform = useRecoilValue(platformAtom);
  const pickMealAt = useRecoilValue(pickMealAtAtom);
  const pickMealEnd = useRecoilValue(pickMealEndAtom);
  const groupHostInfo = useRecoilValue(groupHostInfoAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const [filterMealSearchForm, setFilterMealSearchFrom] = useRecoilState(filterMealSearchFormAtom);

  const { cartId, cartType } = useCartId();

  const navigate = useNavigate();
  const pinnedItems = usePinnedItems(platform.pinnedItems);
  const popularItems = usePopularItems(currentPickMealPoint.stores, pickMealAt, pinnedItems);

  const [searchInput, setSearchInput] = useState<string>(filterMealSearchForm.searchText);

  const {
    isOpen: isGroupCancelModalOpen,
    onClose: onGroupCancelModalClose,
    onOpen: onGroupCancelModalOpen,
  } = useDisclosure();
  const {
    isOpen: isStoreFilterOpen,
    onClose: onStoreFilterClose,
    onOpen: onStoreFilterOpen,
  } = useDisclosure();
  const {
    isOpen: isTagFilterOpen,
    onClose: onTagFilterClose,
    onOpen: onTagFilterOpen,
  } = useDisclosure();

  useEffect(() => {
    setSearchInput(filterMealSearchForm.searchText);
  }, [filterMealSearchForm.searchText]);

  const availableStores = useMemo(() => {
    return currentPickMealPoint.stores.filter((storeId) =>
      platform.displayStores.includes(storeId),
    );
  }, [currentPickMealPoint.stores, platform.displayStores]);

  const searchStores = useMemo(() => {
    if (filterMealSearchForm.storeIds.length) {
      return filterMealSearchForm.storeIds;
    }
    if (filterMealSearchForm.searchText || filterMealSearchForm.tags.length > 0) {
      return availableStores;
    }
    return [];
  }, [
    filterMealSearchForm.searchText,
    filterMealSearchForm.storeIds,
    filterMealSearchForm.tags,
    availableStores,
  ]);

  const { fetchingStatus: fetchingStatusOfSearchStore, storesFulfillSearch } = useSearchStore({
    ...filterMealSearchForm,
    storeIds: searchStores,
  });
  const {
    fetchingStatus: fetchingStatusOfSearchItem,
    searchItems,
    searchItemCount,
  } = useSearchItem({
    ...filterMealSearchForm,
    storeIds: searchStores,
    itemLimit: 20,
  });

  const { storeList, fetchingStatus } = useStoreList(availableStores);

  const fitSearchTextStore = useMemo(
    () =>
      filterMealSearchForm.searchText
        ? storeList
            .filter(({ name }) =>
              name.toLowerCase().includes(filterMealSearchForm.searchText.toLowerCase()),
            )
            .map(({ objectID }) => objectID)
        : [],
    [storeList, filterMealSearchForm.searchText],
  );

  const filteredStores = useMemo(() => {
    const now = new Date();
    return storeList.filter(
      ({ preOrderSetting: { schedule, afterHours, beforeHours }, officialHolidayISOString }) =>
        pickMealAt &&
        isCanPreOrderTime(schedule, afterHours, beforeHours, pickMealAt, now) &&
        isInOperatingTime(schedule, pickMealAt) &&
        isNotInHoliday(
          officialHolidayISOString?.map((dateString) => parseISO(dateString)) || [],
          pickMealAt,
        ),
    );
  }, [storeList, pickMealAt]);

  const composedPopularItems = useMemo(() => {
    const storesId = filteredStores.map(({ objectID }) => objectID);
    return popularItems
      .filter(({ storeId }) => storesId.includes(storeId))
      .filter(
        ({ isAvailable, inventory }) =>
          isAvailable && fp.gt(getDateInventory(inventory, pickMealAt), 0),
      );
  }, [popularItems, filteredStores, pickMealAt]);

  const { orders } = useOrders(auth, platform?.id);

  const handleOnNavigateToPath = (path: string) =>
    navigate(fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/${path}` : `/${path}`);

  return (
    <PageContainer topBoxStyle={{ position: 'static' }}>
      <FixedHeaderSection>
        <LocationBar />
        <PreorderTimeBar value={transformDateRangeToString(pickMealAt, pickMealEnd)} />
        {fp.eq(cartType, CartType.CART_GROUP) ? (
          <GroupingBar onCancel={onGroupCancelModalOpen} />
        ) : (
          <></>
        )}
      </FixedHeaderSection>

      <Box minH="70vh">
        <Box bgColor="gray.100">
          <LocationBar
            onClick={() =>
              !(fp.eq(cartType, CartType.CART_GROUP) && !isHost) &&
              setModalControl((prev) => ({ ...prev, isPickMealPointModalOpen: true }))
            }
            rightHandComponent={
              <Button
                size="sm"
                variant="unstyled"
                color="white"
                fontSize="sm"
                rightIcon={<Icon as={IoChevronForward} />}
                letterSpacing="0.1em"
              >
                選擇取餐地點
              </Button>
            }
          />
          <PreOrderTimeBar
            pickMealTime={transformDateRangeToString(pickMealAt, pickMealEnd)}
            onClick={() =>
              !(fp.eq(cartType, CartType.CART_GROUP) && !isHost) &&
              setModalControl((prev) => ({ ...prev, isPickupSettingModalOPen: true }))
            }
            rightHandComponent={
              <Button
                size="sm"
                variant="unstyled"
                color="primary"
                fontSize="sm"
                rightIcon={<Icon as={IoChevronForward} />}
                letterSpacing="0.1em"
              >
                選擇取餐時間
              </Button>
            }
          />
          {orders.length !== 0 && (
            <OrderHeaderNotice
              orderNum={orders.length}
              handleNavigateToOrders={() => handleOnNavigateToPath('orderList')}
            />
          )}
          <Content>
            <VStack m="auto" maxW="800px" align="stretch" spacing={4} px={3} py={6}>
              <Heading size="md" color="secondary" fontWeight="normal" textAlign="center">
                想吃什麼？
              </Heading>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();

                  setFilterMealSearchFrom((prev) => ({ ...prev, searchText: searchInput || '' }));
                }}
              >
                <SearchInput
                  value={searchInput}
                  type="text"
                  onChange={(e) => setSearchInput(e.target.value)}
                  onSearch={async () => {
                    setFilterMealSearchFrom((prev) => ({
                      ...prev,
                      searchText: searchInput || '',
                    }));
                  }}
                  onClear={() => {
                    setSearchInput('');
                    setFilterMealSearchFrom((prev) => ({
                      ...prev,
                      searchText: '',
                    }));
                  }}
                />
              </form>
              <HStack spacing={8}>
                <Button
                  onClick={onStoreFilterOpen}
                  flex={1}
                  justifyContent="space-between"
                  bgColor="white"
                  boxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)"
                  rounded={10}
                  _hover={{}}
                  _focus={{}}
                  size="lg"
                >
                  <Text isTruncated>
                    {filterMealSearchForm.storeIds
                      .map(
                        (storeId) =>
                          storeList.find(({ objectID }) => objectID === storeId)?.name || '',
                      )
                      .filter((name) => name)
                      .join(',') || '全部餐廳'}
                  </Text>
                  <Icon as={IoChevronDown} color="primary" boxSize="1.5rem" />
                </Button>
                <Button
                  onClick={onTagFilterOpen}
                  flex={1}
                  justifyContent="space-between"
                  bgColor="white"
                  boxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)"
                  rounded={10}
                  _hover={{}}
                  _focus={{}}
                  size="lg"
                >
                  <Text isTruncated>{filterMealSearchForm.tags.join(',') || '全部素食分類'}</Text>
                  <Icon as={IoChevronDown} color="primary" boxSize="1.5rem" />
                </Button>
              </HStack>
            </VStack>
          </Content>
        </Box>
        <Box bg="white">
          <Content minH="70vh">
            <VStack align="stretch" p={4} spacing={8}>
              <ActivityCarousel
                images={platform.banners}
                carouselSeconds={platform.bannerCarouselSeconds}
              />

              {fp.eq(cartType, CartType.CART_GROUP) && (
                <GroupingNotice
                  isHost={isHost}
                  hostName={groupHostInfo.name}
                  note={groupHostInfo.note}
                  onCancel={onGroupCancelModalOpen}
                />
              )}

              {filterMealSearchForm.searchText ||
              filterMealSearchForm.storeIds.length ||
              filterMealSearchForm.tags.length ? (
                <SearchMode
                  fetchingStatusOfSearchStore={fetchingStatusOfSearchStore}
                  fetchingStatusOfSearchItem={fetchingStatusOfSearchItem}
                  searchStores={storeList.filter(
                    ({ objectID }) =>
                      storesFulfillSearch.includes(objectID) ||
                      fitSearchTextStore.includes(objectID),
                  )}
                  searchItemCount={searchItemCount}
                  searchItems={searchItems}
                  onNavigateSearchItem={() => handleOnNavigateToPath('searchItem')}
                  onNavigateSearchStore={() => handleOnNavigateToPath('searchStore')}
                />
              ) : (
                <DisplayMode
                  fetchingStatus={fetchingStatus}
                  filteredStores={storeList}
                  composedPopularItems={composedPopularItems}
                  handleNavigateToStoreList={() => handleOnNavigateToPath('storeList')}
                  handleNavigateToItemList={() => handleOnNavigateToPath('itemList')}
                />
              )}
            </VStack>
          </Content>

          <Box position="sticky" bottom="0">
            <Box px={4}>
              <Content py={2}>
                <Flex>
                  <Spacer />
                  <GoTopButton />
                </Flex>
              </Content>
            </Box>

            <FooterReturnNotice />
            <>
              {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
                <FooterLoginSection />
              ) : (
                <FooterCartStatusBar onNavigate={() => handleOnNavigateToPath('cart')} />
              )}
            </>
          </Box>
          <LeaveOrCancelGroupModal
            isOpen={isGroupCancelModalOpen}
            onClose={onGroupCancelModalClose}
            isHost={isHost}
          />
          <StoreFilterModal
            isOpen={isStoreFilterOpen}
            onClose={onStoreFilterClose}
            onConfirm={(stores: Array<string>) => {
              setFilterMealSearchFrom((prev) => ({ ...prev, storeIds: stores }));
              onStoreFilterClose();
            }}
            selectedStoreIds={filterMealSearchForm.storeIds}
            stores={storeList}
          />
          <TagFilterModal
            isOpen={isTagFilterOpen}
            onClose={onTagFilterClose}
            onConfirm={(tags: Array<string>) => {
              setFilterMealSearchFrom((prev) => ({ ...prev, tags: tags }));
              onTagFilterClose();
            }}
            selectedTags={filterMealSearchForm.tags}
            tags={platform.tags}
          />
        </Box>
      </Box>
    </PageContainer>
  );
}
